"use client"

import * as React from "react"
import { useState } from "react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { cn } from "../../lib/utils"
import { Textarea } from "./textarea"

export interface MarkdownEditorProps {
  defaultValue?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
  mdClassName?: string
  noBorder?: boolean
  mode: 'view' | 'edit'
}

export const MarkdownEditor = React.forwardRef<
  HTMLDivElement,
  MarkdownEditorProps
>(({
  defaultValue = "",
  onChange,
  placeholder = "Enter markdown...",
  className,
  mdClassName,
  mode,
  noBorder = false,
}, ref) => {
  const [value, setValue] = useState(defaultValue)

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
    onChange?.(e.target.value)
  }

  return (
    <div ref={ref} className={cn("w-full h-full flex gap-4", className)}>
      <div className={cn("flex-1 flex flex-col", mode === 'view' ? 'hidden' : '')}>
        <Textarea
          defaultValue={defaultValue}
          onChange={handleChange}
          placeholder={placeholder}
          className={cn(
            "flex-1 w-full p-4 text-sm",
            "border border-input rounded-md",
            "bg-background text-foreground",
            "placeholder:text-muted-foreground",
            "focus:outline-none focus:ring-1 focus:ring-accent/50",
            "resize-none font-mono"
          )}
        />
      </div>

      <div className="flex-1 flex flex-col">
        <div
          className={cn(
            "flex-1 w-full p-6 text-start",
            noBorder ? '' : "border border-input rounded-md",
            "text-foreground",
            "prose prose-sm dark:prose-invert max-w-none",
            "prose-headings:text-foreground prose-headings:font-semibold",
            "prose-p:text-foreground prose-p:leading-relaxed",
            "prose-strong:text-foreground prose-strong:font-semibold",
            "prose-code:text-foreground prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm",
            "prose-pre:bg-muted prose-pre:text-foreground prose-pre:border prose-pre:border-border",
            "prose-blockquote:text-muted-foreground prose-blockquote:border-l-primary",
            "prose-ul:text-foreground prose-ol:text-foreground",
            "prose-li:text-foreground",
            "prose-a:text-primary prose-a:no-underline hover:prose-a:underline",
            "overflow-auto",
            mdClassName || ''
          )}
        >
          {value ? (
            <Markdown remarkPlugins={[remarkGfm]}>{value}</Markdown>
          ) : (
            <p className="text-muted-foreground italic">Nothing to preview</p>
          )}
        </div>
      </div>
    </div>
  )
})

MarkdownEditor.displayName = "MarkdownEditor"

